import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/creatorCommunityApi";
import { communitiesBase, plusePath } from "@/lib/creator-community-embed";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

import { Users, Plus, Search, Loader2, Radio, Lock, Globe, MessageSquare, X, Sparkles, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FileUpload from "@/components/creator-community/FileUpload";
import { motion } from "framer-motion";
import PluseAdSense from "@/components/PluseAdSense";
import "./PluseAds.css";

function CommunityCard({ c }) {
  const appearance = c.appearance || {};
  const cardAppearance = appearance.card || {};

  const cardGradient =
    cardAppearance.gradient_enabled === true
      ? `linear-gradient(${
          cardAppearance.gradient_direction || "135deg"
        }, ${
          cardAppearance.gradient_from || "#2A2A2A"
        }, ${
          cardAppearance.gradient_to || "#111111"
        })`
      : undefined;

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/communities/${c.slug || c.id}`;

    if (navigator.share) {
      navigator.share({
        title: c.name,
        text: c.description || `Check out ${c.name} on DrkC Communities`,
        url: shareUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Community link copied to clipboard");
    }
  };

  return (
    <Link
      to={`${communitiesBase()}/${c.slug || c.id}`}
      data-testid={`community-card-${c.id}`}
      className="group glass rounded-2xl overflow-hidden border border-white/10 hover:border-white/25 transition-all flex flex-col justify-between"
      style={cardGradient ? { background: cardGradient } : undefined}
    >
      <div>
        {/* Banner */}
        <div
          className="h-24 relative"
          style={{
            background: c.banner_url
              ? undefined
              : "linear-gradient(135deg,#2a2140,#3a1f38)",
          }}
        >
          {c.banner_url && (
            <img
              src={c.banner_url || undefined}
              alt=""
              className="w-full h-full object-cover"
            />
          )}

          {c.admin_only && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-[10px] text-amber-300">
              <Lock className="w-3 h-3" /> Admin only
            </span>
          )}
        </div>

        {/* Main Card Content */}
        <div className="p-4 relative">
          <div className="flex items-center gap-4">

            {/* LARGE LEFT ICON */}
            <div className="w-28 h-32 shrink-0 -mt-10 rounded-2xl border-2 border-[#0b0b12] bg-[#15121f] flex items-center justify-center overflow-hidden shadow-xl">
              {c.icon_url ? (
                <img
                  src={c.icon_url || undefined}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <Users className="w-10 h-10 text-white/50" />
              )}
            </div>

            {/* Community Information */}
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-lg leading-tight truncate flex items-center gap-1.5">
                {c.name}
                {c.is_public === false && (
                  <Lock
                    className="w-3.5 h-3.5 text-amber-300 shrink-0"
                    title="Private"
                  />
                )}
              </div>

              <div className="text-xs text-white/40 mt-1 truncate">
                by <span className="text-white/70 font-medium">{c.owner_name || "Unknown"}</span>
              </div>

              <div className="text-sm text-white/50 line-clamp-2 mt-1 min-h-[40px]">
                {c.description || "No description"}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Info */}
      <div className="p-4 pt-0 flex items-center justify-between border-t border-white/5 mt-2">
        <div className="flex items-center gap-3 text-xs text-white/40">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {c.member_count}
          </span>

          {c.reason && (
            <span className="text-[#6EE7FF]/80 truncate">
              {c.reason}
            </span>
          )}

          {c.unread > 0 && (
            <span className="inline-flex items-center gap-1 text-[#FF6AD5]">
              <MessageSquare className="w-3.5 h-3.5" />
              {c.unread} new
            </span>
          )}

          {c.is_member && (
            <span className="text-emerald-400">
              Joined
            </span>
          )}
        </div>

        {/* Share */}
        <button
          onClick={handleShare}
          title="Share Community"
          className="p-1.5 text-white/50 hover:text-white transition-colors"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>
    </Link>
  );
}

export default function CommunitiesPage() {
  const { user } = useAuth();
  const enabled = true; // Store uses the Creator Hub community implementation.
  const navigate = useNavigate();
  const [mine, setMine] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", icon_url: "", banner_url: "", is_public: true });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, m, s] = await Promise.all([
        api.get("/communities", { params: q ? { q } : {} }),
        user ? api.get("/communities/mine") : Promise.resolve({ data: [] }),
        api.get("/communities/suggested", { params: { limit: 6 } }).catch(() => ({ data: [] })),
      ]);
      setDiscover(d.data); setMine(m.data); setSuggested(s.data || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setCreating(true);
    try {
      const { data } = await api.post("/communities", form);
      toast.success("Community created");
      setShowCreate(false);
      navigate(`${communitiesBase()}/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setCreating(false); }
  };

  if (!enabled) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="communities-locked">
        <Users className="w-10 h-10 mx-auto text-white/30 mb-4" />
        <h1 className="font-display text-2xl font-black mb-2">Communities are coming soon</h1>
        <p className="text-white/50">This feature is being rolled out gradually. Check back shortly.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-6 sm:pb-8" data-testid="communities-page">
      {/* Desktop side ads; hidden on smaller screens by PluseAds.css */}
      <aside className="pluse-side-ad-left community-side-ad" aria-label="Advertisement">
        <div className="pluse-ad-box pluse-side-ad">
          <PluseAdSense type="display" />
        </div>
      </aside>
      <aside className="pluse-side-ad-right community-side-ad" aria-label="Advertisement">
        <div className="pluse-ad-box pluse-side-ad">
          <PluseAdSense type="display" />
        </div>
      </aside>
      {/* Top Header Swipeable / Drag-Enabled Navigation Tabs (Pluse & Communities) */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <motion.div 
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.08}
          onDragEnd={(e, info) => {
            if (info.offset.x > 40) {
              navigate(plusePath());
            } else if (info.offset.x < -40) {
              navigate(communitiesBase());
            }
          }}
          className="flex items-center gap-1.5 p-1 glass rounded-2xl cursor-grab active:cursor-grabbing touch-pan-x"
        >
          <a
            href="https://creators.lostdrkc.in/pluse/"
            onClick={(e) => { e.preventDefault(); navigate(plusePath()); }}
            className="px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200"
          >
            Pluse
          </a>
          <a
            href="https://creators.lostdrkc.in/Communities"
            onClick={(e) => { e.preventDefault(); navigate(communitiesBase()); }}
            className="px-3.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 text-black shadow-lg"
            style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}
          >
            Communities
          </a>
        </motion.div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">DrkC Communities</div>
      </div>

      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-white/50 mb-2">
          <Users className="w-3.5 h-3.5" /> Communities
        </div>
        <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight">Join the <span className="gradient-text">conversation</span></h1>
      </div>

      {/* Search and Create Button Side by Side on Mobile and PC */}
      <div className="flex items-center gap-2 sm:gap-4 mb-8">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search communities…"
            data-testid="community-search" className="pl-10 h-10 sm:h-11 rounded-full bg-white/5 border-white/10 text-xs sm:text-sm w-full" />
        </form>
        {user && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-community-btn"
            className="rounded-full text-black font-semibold text-xs sm:text-sm h-10 sm:h-11 px-3.5 sm:px-5 shrink-0 flex items-center gap-1.5 shadow-lg" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
            <Plus className="w-4 h-4 shrink-0" />
            <span className="truncate">New</span>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
      ) : (
        <>
          {mine.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xs sm:text-sm uppercase tracking-widest text-white/40 mb-4">Your communities</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-communities">
                {mine.map((c, i) => (
                  <React.Fragment key={c.id}>
                    <CommunityCard c={c} />
                    {(i + 1) % 3 === 0 && i < mine.length - 1 && (
                      <div className="community-feed-ad community-feed-ad-card">
                        <PluseAdSense type="inFeed" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>
          )}
          {suggested.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xs sm:text-sm uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#FFD54A]" /> Suggested for you
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suggested-communities">
                {suggested.map((c, i) => (
                  <React.Fragment key={c.id}>
                    <CommunityCard c={c} />
                    {(i + 1) % 3 === 0 && i < suggested.length - 1 && (
                      <div className="community-feed-ad community-feed-ad-card">
                        <PluseAdSense type="inFeed" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>
          )}
          <section>
            <h2 className="text-xs sm:text-sm uppercase tracking-widest text-white/40 mb-4">Discover</h2>
            {discover.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-communities">
                {discover.map((c, i) => (
                  <React.Fragment key={c.id}>
                    <CommunityCard c={c} />
                    {(i + 1) % 3 === 0 && i < discover.length - 1 && (
                      <div className="community-feed-ad community-feed-ad-card">
                        <PluseAdSense type="inFeed" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="glass rounded-3xl p-10 text-center">
                <Radio className="w-8 h-8 mx-auto text-white/30 mb-3" />
                <div className="font-display font-semibold text-lg mb-1">No communities yet</div>
                <div className="text-sm text-white/50">Be the first to create one.</div>
              </div>
            )}
          </section>
        </>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="glass-strong w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 border border-white/10 max-h-[90vh] overflow-y-auto" data-testid="create-community-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg sm:text-xl font-bold">Create Community</h3>
              <button onClick={() => setShowCreate(false)} className="p-2 rounded-full glass glass-hover"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Community name" data-testid="community-name-input" className="bg-white/5 border-white/10 text-sm h-10" />
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's this community about?" data-testid="community-desc-input" className="bg-white/5 border-white/10 text-sm" />
              <div>
                <div className="text-xs text-white/50 mb-1.5">Icon</div>
                <FileUpload kind="image" onUploaded={(d) => setForm({ ...form, icon_url: d?.url || "" })} buttonLabel="Upload icon" testid="community-icon-upload" />
              </div>
              <div>
                <div className="text-xs text-white/50 mb-1.5">Banner</div>
                <FileUpload kind="image" onUploaded={(d) => setForm({ ...form, banner_url: d?.url || "" })} buttonLabel="Upload banner" testid="community-banner-upload" />
              </div>
              <div>
                <div className="text-xs text-white/50 mb-1.5">Privacy</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setForm({ ...form, is_public: true })} data-testid="community-public-btn"
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left ${form.is_public ? "border-[#9387FE] bg-[#9387FE]/10" : "border-white/10 bg-white/5"}`}>
                    <Globe className="w-4 h-4 text-[#6EE7FF] shrink-0" />
                    <div><div className="text-sm font-medium">Public</div><div className="text-[11px] text-white/40">Anyone can join instantly</div></div>
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, is_public: false })} data-testid="community-private-btn"
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left ${!form.is_public ? "border-[#9387FE] bg-[#9387FE]/10" : "border-white/10 bg-white/5"}`}>
                    <Lock className="w-4 h-4 text-amber-300 shrink-0" />
                    <div><div className="text-sm font-medium">Private</div><div className="text-[11px] text-white/40">Members join by request</div></div>
                  </button>
                </div>
              </div>
              <Button onClick={create} disabled={creating} data-testid="community-create-submit"
                className="w-full rounded-full text-black font-semibold h-11 text-sm" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Community"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}